import { createAsyncThunk } from '@reduxjs/toolkit'

import effects from 'app/effects/effects'
import MessageAlert from 'app/components/MessageAlert'
import { HEADERS_IMPORT_CSV_FILE } from 'app/main/subscribersV4/AddSubscibers/components/newImport/constansts'
import {
  generateFieldErrorsSingleSubscriber,
  mappedChangedHeaders,
  markDuplicatesByKeys,
  reverseMappedHeaders,
} from './helpers'
import {
  setImportedSubscribersIds,
  setUploadingEnd,
  setUploadingProgress,
  setUploadingStart,
  setValidatingStart,
  setImportingStart,
  setImportingEnd,
  setInitilization,
} from '.'

const sendCSVFileDataToValidate = async (CSVData, { getState, dispatch }) => {
  try {
    const { importCSV } = getState()
    let rows,
      headers = []
    if (CSVData) {
      rows = CSVData.data.rows
      headers = CSVData.data.headers
    } else {
      rows = importCSV.data.rows
      headers = importCSV.data.headers
    }
    const mappedRows = mappedChangedHeaders({ rows, headers })
    const { signedURL, key: s3ObjectKey } =
      await effects.apis.getUploadFileUrl()
    if (!signedURL || !s3ObjectKey) {
      throw new Error('Error validate uploading CSV file')
    }
    const uploadingAxiosController = new AbortController()
    dispatch(setUploadingStart(uploadingAxiosController))
    await effects.apis.uploadJsonToS3({
      url: signedURL,
      jsonObject: mappedRows,
      progressCallback: (progress) => {
        dispatch(setUploadingProgress(progress))
      },
      controller: uploadingAxiosController,
    })
    dispatch(setUploadingEnd())
    const validatingAxiosController = new AbortController()
    dispatch(setValidatingStart(validatingAxiosController))

    await saveCSVData(importCSV.data)
    await effects.apis.importCSV.validate(
      {
        s3ObjectKey,
      },
      validatingAxiosController,
    )
  } catch (error) {
    const isGatewayTimoutErrorCode = error?.response?.status === 504
    if (isGatewayTimoutErrorCode) return
    const { importCSV } = getState()
    const errorMessage = importCSV.validationErrorMessage
    dispatch(cancelImportThunk({ errorMessage }))
    dispatch(
      MessageAlert.showError({
        message: errorMessage,
      }),
    )
  }
}

export const sendCSVFileDataToValidateThunk = createAsyncThunk(
  'importCSVData/sendToValidate',
  sendCSVFileDataToValidate,
)

const sendSingleRowToValidate = async (
  { changedSubscriber, index, isUpdatedFromModal = false },
  { getState, signal },
) => {
  const uniqueKeys = [
    'unique_id',
    'email',
    'phone_number',
    'automated_call_number',
  ]

  try {
    const { importCSV } = getState()
    const {
      data: { rows, headers, validationInfo },
    } = importCSV

    const { errors, ...rest } = changedSubscriber
    const [data] = mappedChangedHeaders({
      rows: [rest],
      headers,
    })

    const responseValidateData = await effects.apis.importCSV.singleValidate({
      subscriber: data,
      index,
      processId: validationInfo.importId,
      errors,
      signal,
    })

    const updatedData = isUpdatedFromModal
      ? reverseMappedHeaders({
          rows: [changedSubscriber],
          headers,
          oldRows: [rows[index]],
        })[0]
      : changedSubscriber

    changedSubscriber = generateFieldErrorsSingleSubscriber(
      updatedData,
      responseValidateData,
    )
    const newRows = [...rows]
    const oldSubscriber = newRows[index]
    newRows[index] = changedSubscriber
    const checkedRowsForDuplicates = markDuplicatesByKeys(
      newRows,
      oldSubscriber,
      changedSubscriber,
      uniqueKeys,
    )
    return { checkedRowsForDuplicates }
  } catch (err) {
    if (!signal.aborted) {
      console.log('error:', err)
    }
  }
}

export const sendSingleRowToValidateThunk = createAsyncThunk(
  'importCSVData/sendSingleRowValidate',
  sendSingleRowToValidate,
)

const importSubscribers = async (_, { getState, dispatch }) => {
  try {
    const { importCSV } = getState()
    const {
      data: { rows, headers, sendInvitation, validationInfo, tags },
    } = importCSV

    const constantsKeys = HEADERS_IMPORT_CSV_FILE.map(({ header }) => header)

    const validSubscribers = rows
      .filter(({ errors }) => !errors?.isRequiredFields)
      .map((subscriber) => {
        const newSubscriber = constantsKeys.reduce(
          (acc, header) => {
            acc[header] = acc[header] ?? ''
            return acc
          },
          { ...mappedChangedHeaders({ rows: [subscriber], headers })[0] },
        )

        const { errors, ...rest } = newSubscriber
        return rest
      })

    const { signedURL, key: s3ObjectKey } =
      await effects.apis.getUploadFileUrl()

    if (!signedURL || !s3ObjectKey) {
      throw new Error('Error uploading CSV file')
    }

    const uploadingAxiosController = new AbortController()
    dispatch(setUploadingStart(uploadingAxiosController))
    await effects.apis.uploadJsonToS3({
      url: signedURL,
      jsonObject: validSubscribers,
      progressCallback: (progress) => {
        dispatch(setUploadingProgress(progress))
      },
      controller: uploadingAxiosController,
    })
    dispatch(setUploadingEnd({}))
    const importedSubscribersIds = validSubscribers.map(
      (subscriber) => subscriber.id,
    )
    dispatch(setImportedSubscribersIds(importedSubscribersIds))
    const importingAxiosController = new AbortController()
    dispatch(setImportingStart(importingAxiosController))
    await saveCSVData({ ...importCSV.data, importedSubscribersIds })
    await effects.apis.importCSV.importSubscribers(
      {
        s3ObjectKey,
        sendInvitation,
        tags,
        previousProcessId: validationInfo.previousProcessId,
      },
      importingAxiosController,
    )
    dispatch(setImportingEnd({}))
  } catch (err) {
    console.log(err, 'err')
  }
}

export const importSubscribersThunk = createAsyncThunk(
  'importCSVData/importSubscribers',
  importSubscribers,
)

const cancelImport = async ({ errorMessage }, { getState, dispatch }) => {
  try {
    const { importCSV } = getState()
    const {
      data: {
        validationInfo: { importId },
      },
      uploadingAxiosController,
      validatingAxiosController,
      importingAxiosController,
    } = importCSV
    if (uploadingAxiosController) {
      uploadingAxiosController.abort()
    }
    if (validatingAxiosController) {
      validatingAxiosController.abort()
    }
    if (importingAxiosController) {
      importingAxiosController.abort()
    }
    if (importId) {
      await effects.apis.importCSV.cancelImport({ importId })
    }
  } catch (err) {
    errorMessage &&
      dispatch(
        MessageAlert.showError({
          message: errorMessage,
        }),
      )
  }
}

export const cancelImportThunk = createAsyncThunk(
  'importCSVData/cancelImport',
  cancelImport,
)

const databaseName = 'ImportCSVDatabase'
const objectStoreName = 'rows'
const storageKey = 'progress'

// Function to save CSV data
export function saveCSVData(data) {
  return new Promise((resolve, reject) => {
    // First, open the database without specifying a version to get the current version
    const openRequest = indexedDB.open(databaseName)

    openRequest.onsuccess = (event) => {
      // Get the current version from the existing database
      const currentVersion = event.target.result.version
      // Close this connection
      event.target.result.close()

      // Now open with the correct version
      const request = indexedDB.open(databaseName, currentVersion)

      request.onupgradeneeded = (event) => {
        const db = event.target.result
        if (!db.objectStoreNames.contains(objectStoreName)) {
          db.createObjectStore(objectStoreName, { keyPath: 'storageKey' })
        }
      }

      request.onsuccess = (event) => {
        const db = event.target.result

        try {
          // Check if the store exists before trying to use it
          if (!db.objectStoreNames.contains(objectStoreName)) {
            // If we're here, the database exists but somehow the store wasn't created
            db.close()

            // Increment version to trigger onupgradeneeded
            const reopenRequest = indexedDB.open(
              databaseName,
              currentVersion + 1,
            )

            reopenRequest.onupgradeneeded = (e) => {
              const newDb = e.target.result
              newDb.createObjectStore(objectStoreName, {
                keyPath: 'storageKey',
              })
            }

            reopenRequest.onsuccess = (e) => {
              const newDb = e.target.result
              const transaction = newDb.transaction(
                [objectStoreName],
                'readwrite',
              )
              const store = transaction.objectStore(objectStoreName)

              const storeRequest = store.put({
                storageKey,
                data,
              })

              storeRequest.onsuccess = () => {
                newDb.close()
                resolve()
              }

              storeRequest.onerror = () => {
                newDb.close()
                reject(storeRequest.error)
              }
            }

            reopenRequest.onerror = () => reject(reopenRequest.error)
          } else {
            // Normal path - store exists, proceed with storing data
            const transaction = db.transaction([objectStoreName], 'readwrite')
            const store = transaction.objectStore(objectStoreName)

            const storeRequest = store.put({
              storageKey,
              data,
            })

            storeRequest.onsuccess = () => {
              db.close()
              resolve()
            }

            storeRequest.onerror = () => {
              db.close()
              reject(storeRequest.error)
            }
          }
        } catch (error) {
          db.close()
          reject(error)
        }
      }

      request.onerror = (event) => {
        reject(request.error)
      }
    }

    openRequest.onerror = () => reject(openRequest.error)
  })
}

// Function to retrieve the entire CSV dataset
export function loadCSVData() {
  return new Promise((resolve, reject) => {
    // First, open the database without specifying a version to get the current version
    const openRequest = indexedDB.open(databaseName)

    openRequest.onsuccess = (event) => {
      // Get the current version from the existing database
      const currentVersion = event.target.result.version
      // Close this connection
      event.target.result.close()

      // Now open with the correct version
      const request = indexedDB.open(databaseName, currentVersion)

      request.onsuccess = (event) => {
        const db = event.target.result

        try {
          // Check if the store exists
          if (!db.objectStoreNames.contains(objectStoreName)) {
            db.close()
            resolve(null) // No data found because store doesn't exist
            return
          }

          const transaction = db.transaction([objectStoreName], 'readonly')
          const store = transaction.objectStore(objectStoreName)
          const getRequest = store.get(storageKey)

          getRequest.onsuccess = () => {
            if (getRequest.result) {
              db.close()
              resolve(getRequest.result.data)
            } else {
              db.close()
              resolve(null) // No data found
            }
          }

          getRequest.onerror = () => {
            db.close()
            reject(getRequest.error)
          }
        } catch (error) {
          db.close()
          reject(error)
        }
      }

      request.onerror = () => reject(request.error)
    }

    openRequest.onerror = () => reject(openRequest.error)
  })
}

export function clearCSVData() {
  return new Promise((resolve, reject) => {
    // First, just open the database without specifying a version
    const openRequest = indexedDB.open(databaseName)

    openRequest.onsuccess = (event) => {
      // Get the current version from the existing database
      const currentVersion = event.target.result.version
      // Close this connection
      event.target.result.close()

      // Now open with the correct version
      const request = indexedDB.open(databaseName, currentVersion)

      request.onsuccess = (event) => {
        const db = event.target.result

        // Check if the object store exists
        if (!db.objectStoreNames.contains(objectStoreName)) {
          db.close()
          resolve() // Nothing to clear
          return
        }

        try {
          const transaction = db.transaction([objectStoreName], 'readwrite')
          const store = transaction.objectStore(objectStoreName)
          const deleteRequest = store.delete(storageKey)

          deleteRequest.onsuccess = () => {
            db.close()
            resolve()
          }

          deleteRequest.onerror = () => {
            db.close()
            reject(deleteRequest.error)
          }
        } catch (error) {
          db.close()
          reject(error)
        }
      }

      request.onerror = () => reject(request.error)
    }

    openRequest.onerror = () => reject(openRequest.error)
  })
}
